import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../context/AuthContext'
import {
  Check, Copy, FolderPlus, Inbox, Loader2, Mail, MessageCircle, Phone, UserCheck, X,
} from 'lucide-react'

/* Bacheca: le richieste di lavoro arrivate dalla pagina pubblica /richiesta */

const STATI = [
  { value: 'nuova', label: 'Nuove', badge: 'bg-violet-600 text-white' },
  { value: 'in_lettura', label: 'In lettura', badge: 'bg-sky-500/20 text-sky-400' },
  { value: 'accettata', label: 'Accettate', badge: 'bg-emerald-500/20 text-emerald-400' },
  { value: 'rifiutata', label: 'Rifiutate', badge: 'bg-gray-700 text-gray-300' },
  { value: 'archiviata', label: 'Archiviate', badge: 'bg-gray-800 text-gray-500' },
]

const CATEGORIE = {
  animazione: 'Animazione',
  vfx: 'VFX',
  montaggio: 'Montaggio',
  giochi: 'Giochi e VR',
  strumenti: 'Strumenti',
  non_so: 'Da capire',
}

const statoConfig = (v) => STATI.find(s => s.value === v) ?? STATI[0]

const fmtData = (iso) => {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

const soloNumeri = (tel) => (tel || '').replace(/\D/g, '')

const Bacheca = () => {
  const { profile } = useAuth()
  const [richieste, setRichieste] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState('nuova')
  const [apertaId, setApertaId] = useState(null)

  const fetchRichieste = useCallback(async () => {
    const { data } = await supabase
      .from('richieste')
      .select('*, profiles:assegnata_a(id, nickname, full_name)')
      .order('created_at', { ascending: false })
    setRichieste(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetchRichieste() }, [fetchRichieste])

  /* Aggiornamento in tempo reale: se arriva una richiesta mentre siamo qui, compare da sola */
  useEffect(() => {
    const ch = supabase.channel('bacheca-richieste')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'richieste' }, () => fetchRichieste())
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [fetchRichieste])

  const conteggi = useMemo(() => {
    const out = { tutte: richieste.length }
    STATI.forEach(s => { out[s.value] = richieste.filter(r => r.stato === s.value).length })
    return out
  }, [richieste])

  const visibili = filtro === 'tutte' ? richieste : richieste.filter(r => r.stato === filtro)
  const aperta = richieste.find(r => r.id === apertaId) || null

  const aggiorna = async (id, patch) => {
    setRichieste(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)))
    await supabase.from('richieste').update(patch).eq('id', id)
    fetchRichieste()
  }

  const apri = (r) => {
    setApertaId(r.id)
    const patch = {}
    if (!r.letta_at) patch.letta_at = new Date().toISOString()
    if (r.stato === 'nuova') patch.stato = 'in_lettura'
    if (Object.keys(patch).length) aggiorna(r.id, patch)
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <Inbox size={22} className="text-violet-400" />
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Bacheca</h1>
      </div>
      <p className="text-gray-500 text-sm sm:text-base mb-6">
        Le richieste di lavoro arrivate dal sito. Da qui rispondi, assegni e trasformi una richiesta in progetto.
      </p>

      {/* Filtri */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 sm:flex-wrap mb-5">
        <button
          type="button"
          onClick={() => setFiltro('tutte')}
          className={`flex-shrink-0 px-4 h-10 rounded-lg text-sm font-medium transition-colors ${
            filtro === 'tutte' ? 'bg-violet-600 text-white' : 'bg-gray-900 border border-gray-800 text-gray-400 hover:text-white'
          }`}
        >
          Tutte <span className="opacity-60">{conteggi.tutte}</span>
        </button>
        {STATI.map(s => (
          <button
            key={s.value}
            type="button"
            onClick={() => setFiltro(s.value)}
            className={`flex-shrink-0 px-4 h-10 rounded-lg text-sm font-medium transition-colors ${
              filtro === s.value ? 'bg-violet-600 text-white' : 'bg-gray-900 border border-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            {s.label} <span className="opacity-60">{conteggi[s.value]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={24} className="animate-spin text-violet-500" />
        </div>
      ) : visibili.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center">
          <p className="text-gray-400">Nessuna richiesta in questo stato.</p>
          <p className="text-gray-600 text-sm mt-1">
            Le richieste arrivano dalla pagina pubblica del sito.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {visibili.map(r => {
            const cfg = statoConfig(r.stato)
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => apri(r)}
                className="text-left bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-2xl p-5 transition-colors"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="text-white font-semibold">
                    {r.nome} {r.cognome}
                    {r.azienda && <span className="text-gray-500 font-normal"> · {r.azienda}</span>}
                  </p>
                  <span className={`text-[11px] font-semibold px-2 py-1 rounded-full flex-shrink-0 ${cfg.badge}`}>
                    {cfg.label.replace(/e$/, 'a')}
                  </span>
                </div>
                <p className="text-sm text-violet-400 font-medium">{CATEGORIE[r.categoria] ?? r.categoria}</p>
                <p className="text-sm text-gray-400 mt-2 line-clamp-3 break-words">{r.descrizione}</p>
                <div className="flex items-center gap-3 mt-3 text-xs text-gray-600">
                  <span>{fmtData(r.created_at)}</span>
                  {r.profiles && <span className="text-gray-500">· {r.profiles.nickname || r.profiles.full_name}</span>}
                  {r.progetto_id && <span className="text-emerald-500">· progetto creato</span>}
                </div>
              </button>
            )
          })}
        </div>
      )}

      {aperta && (
        <Dettaglio
          richiesta={aperta}
          profile={profile}
          onClose={() => setApertaId(null)}
          onAggiorna={aggiorna}
          onRefresh={fetchRichieste}
        />
      )}
    </div>
  )
}

/* ── Dettaglio richiesta ── */

const Dettaglio = ({ richiesta: r, profile, onClose, onAggiorna, onRefresh }) => {
  const [note, setNote] = useState(r.note_interne || '')
  const [creando, setCreando] = useState(false)
  const [linkCopiato, setLinkCopiato] = useState(false)
  const [errore, setErrore] = useState('')

  const salvaNote = () => {
    if (note === (r.note_interne || '')) return
    onAggiorna(r.id, { note_interne: note || null })
  }

  const creaProgetto = async () => {
    setCreando(true)
    setErrore('')
    const titolo = `${r.azienda || `${r.nome} ${r.cognome}`} - ${CATEGORIE[r.categoria] ?? r.categoria}`
    const descrizione = [
      r.descrizione,
      '',
      `Richiesta dal sito il ${fmtData(r.created_at)}`,
      `Contatto: ${r.nome} ${r.cognome}, ${r.email}, ${r.telefono}`,
      r.budget ? `Budget indicato: ${r.budget}` : null,
      r.riferimenti ? `Riferimenti: ${r.riferimenti}` : null,
    ].filter(Boolean).join('\n')

    const { data, error } = await supabase
      .from('progetti')
      .insert({
        titolo,
        descrizione,
        scadenza: r.scadenza || null,
        created_by: profile.id,
      })
      .select()
      .single()

    if (error) { setErrore('Progetto non creato: ' + error.message); setCreando(false); return }

    await supabase.from('project_members').insert({ project_id: data.id, user_id: profile.id, role: 'owner' })
    await onAggiorna(r.id, { progetto_id: data.id, stato: 'accettata' })
    await onRefresh()
    setCreando(false)
  }

  const copiaLinkInvito = async () => {
    setErrore('')
    const { data, error } = await supabase
      .from('project_invites')
      .insert({ project_id: r.progetto_id, created_by: profile.id, role: 'viewer' })
      .select()
      .single()
    if (error || !data) { setErrore('Link non creato: riprova'); return }
    const url = `${window.location.origin}/invito/${data.token}`
    navigator.clipboard.writeText(url)
    setLinkCopiato(true)
    setTimeout(() => setLinkCopiato(false), 2500)
  }

  const tel = soloNumeri(r.telefono)
  const mio = r.assegnata_a === profile?.id

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center sm:p-4 overflow-y-auto">
      <div className="bg-gray-900 border border-gray-800 w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-5 py-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-white">{r.nome} {r.cognome}</h2>
            <p className="text-sm text-gray-500">
              {r.azienda ? r.azienda + ' · ' : ''}{CATEGORIE[r.categoria] ?? r.categoria} · {fmtData(r.created_at)}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Contatti rapidi */}
          <div className="flex flex-wrap gap-2">
            <a href={`mailto:${r.email}`}
              className="inline-flex max-w-full items-center gap-2 break-all bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-lg px-4 py-2 min-h-11 transition-colors">
              <Mail size={16} /> {r.email}
            </a>
            <a href={`tel:${r.telefono}`}
              className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-lg px-4 h-11 transition-colors">
              <Phone size={16} /> {r.telefono}
            </a>
            {tel && (
              <a href={`https://wa.me/${tel}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-lg px-4 h-11 transition-colors">
                <MessageCircle size={16} /> WhatsApp
              </a>
            )}
          </div>

          {/* Il progetto */}
          <div>
            <p className="text-sm text-gray-500 mb-2">Il progetto</p>
            <p className="text-gray-200 whitespace-pre-line break-words leading-relaxed">{r.descrizione}</p>
            <dl className="grid sm:grid-cols-3 gap-4 mt-4">
              <div>
                <dt className="text-xs text-gray-600">Scadenza</dt>
                <dd className="text-sm text-gray-300">{r.scadenza || 'non indicata'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-600">Budget</dt>
                <dd className="text-sm text-gray-300">{r.budget || 'non indicato'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-600">Arrivata da</dt>
                <dd className="text-sm text-gray-300">{r.origine || 'sito'}</dd>
              </div>
            </dl>
            {r.riferimenti && (
              <div className="mt-4">
                <p className="text-xs text-gray-600 mb-1">Riferimenti</p>
                <p className="text-sm text-gray-300 whitespace-pre-line break-words">{r.riferimenti}</p>
              </div>
            )}
          </div>

          {/* Stato */}
          <div>
            <p className="text-sm text-gray-500 mb-2">Stato</p>
            <div className="flex flex-wrap gap-2">
              {STATI.map(s => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => onAggiorna(r.id, { stato: s.value })}
                  className={`px-4 h-10 rounded-lg text-sm font-medium transition-colors ${
                    r.stato === s.value ? 'bg-violet-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                >
                  {s.label.replace(/e$/, 'a')}
                </button>
              ))}
            </div>
          </div>

          {/* Assegnazione */}
          <div>
            <p className="text-sm text-gray-500 mb-2">Se ne occupa</p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => onAggiorna(r.id, { assegnata_a: mio ? null : profile.id })}
                className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-lg px-4 h-11 transition-colors"
              >
                <UserCheck size={16} /> {mio ? 'Lascia la richiesta' : 'Prendo io'}
              </button>
              {r.profiles && !mio && (
                <span className="text-sm text-gray-400">{r.profiles.nickname || r.profiles.full_name}</span>
              )}
            </div>
          </div>

          {/* Note interne */}
          <div>
            <label htmlFor="note-interne" className="block text-sm text-gray-500 mb-2">
              Note interne (il cliente non le vede)
            </label>
            <textarea
              id="note-interne"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={salvaNote}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-base text-white placeholder-gray-500 focus:border-violet-500 focus:outline-none"
              placeholder="Cosa ci siamo detti, prezzo ipotizzato, chi lo segue"
            />
          </div>

          {/* Azioni finali */}
          <div className="border-t border-gray-800 pt-5 space-y-3">
            {!r.progetto_id ? (
              <button
                type="button"
                onClick={creaProgetto}
                disabled={creando}
                className="w-full inline-flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-60 text-white font-medium rounded-xl h-12 transition-colors"
              >
                {creando ? <Loader2 size={18} className="animate-spin" /> : <FolderPlus size={18} />}
                Crea il progetto da questa richiesta
              </button>
            ) : (
              <button
                type="button"
                onClick={copiaLinkInvito}
                className="w-full inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-200 font-medium rounded-xl h-12 transition-colors"
              >
                {linkCopiato ? <Check size={18} className="text-emerald-400" /> : <Copy size={18} />}
                {linkCopiato ? 'Link copiato' : 'Copia il link di invito per il cliente'}
              </button>
            )}
            {errore && <p className="text-sm text-red-400">{errore}</p>}
            <p className="text-xs text-gray-600">
              Il link di invito fa entrare il cliente nella sua area: vede solo questo progetto.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Bacheca
